import React, { useEffect, useState } from "react";
import { Save, Upload, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Field, OLIVE, errMsg, fmtDateTime } from "./dpaUtils";

const FIELDS = [
  ["trading_name", "Trading name"], ["legal_name", "Full registered legal entity name"], ["company_number", "Company registration number (where applicable)"],
  ["registered_office", "Registered office address", true], ["correspondence_address", "Business correspondence address", true],
  ["contact_email", "General contact email"], ["dp_contact_email", "Data protection contact email"], ["website", "Website"],
  ["signatory_name", "Authorised signatory name"], ["signatory_title", "Authorised signatory job title"],
  ["jurisdiction", "Governing law / jurisdiction (subject to legal review)"],
];

const Logo = ({ version }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    api.get("/dpa/provider/logo", { responseType: "blob" }).then(({ data }) => setSrc(URL.createObjectURL(data))).catch(() => setSrc(null));
  }, [version]);
  return src ? <img src={src} alt="Castlewood logo" className="h-14 object-contain border p-1 bg-white" data-testid="provider-logo-img" /> : <div className="text-xs text-muted-foreground">No logo uploaded</div>;
};

const ApplicationCard = () => {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/dpa/application").then(({ data }) => setA(data)); }, []);
  if (!a) return null;
  const save = async () => {
    try { setA((await api.put("/dpa/application", a)).data); toast.success("Software Service saved"); }
    catch (e) { toast.error("Save failed", { description: errMsg(e) }); }
  };
  return (
    <Card className="rounded-none p-6 space-y-3" data-testid="application-card">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Software Service covered by this application</div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Application name (shown as 'Software Service')" required value={a.name} onChange={(v) => setA({ ...a, name: v })} testId="application-name-input" />
        <Field label="Short description" value={a.description} onChange={(v) => setA({ ...a, description: v })} testId="application-description-input" />
      </div>
      <div className="text-[11px] text-muted-foreground">Application ID: <span className="font-mono">{a.slug}</span>. The application is a Castlewood product, not a separate legal entity.</div>
      <Button className="rounded-none text-white" style={OLIVE} onClick={save} data-testid="save-application-btn"><Save className="h-4 w-4 mr-1" />Save</Button>
    </Card>
  );
};

export const ProviderProfileTab = () => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const load = () => api.get("/dpa/provider").then(({ data }) => { setD(data); setForm({ ...data.profile, change_note: "", effective_from: "" }); });
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const save = async () => {
    try {
      await api.put("/dpa/provider", { ...form, effective_from: form.effective_from || null });
      toast.success("Castlewood profile saved", { description: "New agreements will use these details. Issued agreements are unchanged." });
      load();
    } catch (e) { toast.error("Save failed", { description: errMsg(e) }); }
  };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/dpa/provider/logo", fd); toast.success("Logo uploaded"); load(); }
    catch (e) { toast.error("Upload failed", { description: errMsg(e) }); }
  };
  return (
    <div className="space-y-5">
      <ApplicationCard />
      <Card className="rounded-none p-6 space-y-4" data-testid="provider-profile-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Castlewood Software Profile · v{d.profile.version}</div>
            <div className="text-xs text-muted-foreground mt-1">Effective from {d.profile.effective_from || "—"} · last changed {fmtDateTime(d.profile.updated_at)} by {d.profile.updated_by}</div>
          </div>
          {d.missing.length
            ? <div className="text-xs text-amber-700 flex items-center gap-1" data-testid="provider-missing"><AlertTriangle className="h-4 w-4" />{d.missing.length} required field(s) missing — agreements can't be issued</div>
            : <div className="text-xs text-emerald-700 flex items-center gap-1" data-testid="provider-complete"><CheckCircle2 className="h-4 w-4" />Complete</div>}
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {FIELDS.map(([k, l, multi]) => (
            <Field key={k} label={l} multiline={multi} required={d.required.includes(k)} value={form[k]} onChange={(v) => setForm({ ...form, [k]: v })} testId={`provider-field-${k}`} />
          ))}
          <Field label="Change effective from" type="date" value={form.effective_from} onChange={(v) => setForm({ ...form, effective_from: v })} testId="provider-effective-from" hint="Defaults to today" />
          <Field label="Reason for change" value={form.change_note} onChange={(v) => setForm({ ...form, change_note: v })} testId="provider-change-note" />
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <Logo version={d.profile.version} />
          <label className="text-sm cursor-pointer inline-flex items-center gap-1 border px-3 py-1.5 hover:bg-slate-50" data-testid="provider-logo-upload">
            <Upload className="h-4 w-4" />Upload logo (PNG/JPEG)
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          </label>
          <Button className="rounded-none text-white ml-auto" style={OLIVE} onClick={save} data-testid="save-provider-btn"><Save className="h-4 w-4 mr-1" />Save profile</Button>
        </div>
      </Card>
      <Card className="rounded-none p-6" data-testid="provider-history">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Change history</div>
        {d.history.map((h) => (
          <div key={h.version} className="text-xs border-t py-2" style={{ borderColor: "var(--hi-border)" }}>
            <span className="font-semibold">v{h.version}</span> · effective {h.effective_from} · {fmtDateTime(h.updated_at)} · {h.updated_by}{h.change_note && ` · “${h.change_note}”`}
            <div className="text-muted-foreground">{Object.keys(h.changes_after || {}).map((k) => `${k}: “${h.changes_before?.[k] || ""}” → “${h.changes_after[k] || ""}”`).join(" · ")}</div>
          </div>
        ))}
        {!d.history.length && <div className="text-xs text-muted-foreground">No changes yet.</div>}
      </Card>
    </div>
  );
};
